import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Minus, Plus } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../../api/api";
//old import { quote, monthEnd } from "../../pages/org/OrgLicenses";
import { quote, monthEnd, licenseParts } from "../../pages/org/OrgLicenses";
//old import { Alert, Field, OrgModal, Pill, apiError, formatDate, useOrgStyles, ORG_TYPE_LABEL } from "./orgUi";
import { Alert, Field, OrgModal, Pill, apiError, formatDate, useOrgStyles, ORG_TYPE_LABEL, LICENSE_TYPE_LABEL } from "./orgUi";

/*
 * One window for an organization's licenses (JJ, Oct 23: one place, like "add or remove
 * licenses" in Google Workspace or Microsoft 365): for student and coach licenses, what the
 * org has and pays now, the number it should have, and what that costs and when it starts.
 * It changes them with the existing steps: Set up licenses (more; the org pays on its Licenses
 * page), the plan's seats (fewer, from the next renewal), its automatic renewal (the paid
 * licenses stop at renewal) and the org's own included licenses. Pono Pilot admins open
 * it from Admin -> Organizations, the License requests tab and the org's Licenses page.
 */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const lic = (n) => `${n} license${n === 1 ? "" : "s"}`;
const per = (mode) => (mode === "yearly" ? "a year" : "a month");
const pctText = (pct) => `${Math.round(Number(pct) * 100) / 100}%`;
// Community Partner packages (Oct 16): 25 licenses free, 50 and 100 in all with the rest paid
const PARTNER_FREE = 25;
const PARTNER_PACKAGES = [25, 50, 100];
const KINDS = [
    //old { key: "student", title: "Student licenses", plural: "students", base: "StudentLicenseLimit", purchased: "StudentLicensesPurchased", used: "StudentLicensesUsed", field: "studentLicenseLimit" },
    { key: "student", title: "Client licenses", plural: "clients", base: "StudentLicenseLimit", purchased: "StudentLicensesPurchased", used: "StudentLicensesUsed", field: "studentLicenseLimit" },
    { key: "coach", title: "Coach licenses", plural: "coaches", base: "CoachLicenseLimit", purchased: "CoachLicensesPurchased", used: "CoachLicensesUsed", field: "coachLicenseLimit" },
];
const MAX_LICENSES = 2000;
const MAX_PLAN_SEATS = 1000; // paid licenses on one plan (the backend's MAX_PLAN_SEATS)
/** The day after a month's last day ("2026-10-31" -> "2026-11-01"). */
const dayAfter = (date) => {
    const d = new Date(`${String(date).slice(0, 10)}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

/** The smallest bigger number of paid seats that costs less (a volume step prices every seat), or null. */
function cheaperBigger(pricing, seats) {
    const total = quote(pricing, seats).total;
    return (pricing.Discounts || []).map((d) => Number(d.MinQuantity)).filter((m) => m > seats).sort((a, b) => a - b)
        .map((m) => ({ seats: m, total: quote(pricing, m).total })).find((c) => c.total < total) || null;
}

/** A step the window refuses itself (its message is shown as it is). */
class Refused extends Error {}

export default function LicensesModal({ orgId, onClose, onChanged }) {
    const s = useOrgStyles();
    const [org, setOrg] = useState(null);
    const [data, setData] = useState(null); // { pricing, requests }
    const [error, setError] = useState(""); // the window couldn't load
    // what a change did, or why it didn't: shown in the license type's section, by the button that was used
    const [result, setResult] = useState(null); // { kind, error | notice }
    const [busy, setBusy] = useState(false);
    const [version, setVersion] = useState(0); // a change resets both sections to what's saved

    const load = useCallback(async () => {
        const [o, r] = await Promise.all([api.get(`/org/${orgId}`), api.get(`/org/${orgId}/license-requests`)]);
        setOrg(o.data.organization);
        setData({ pricing: r.data.pricing || {}, requests: r.data.requests || [] });
    }, [orgId]);

    useEffect(() => {
        load().catch((err) => setError(apiError(err, "Couldn't load this organization's licenses.")));
    }, [load]);

    // Every change goes through here, one at a time; then the window and the page behind it reload
    const run = async (kind, fn, done) => {
        setBusy(true);
        setResult(null);
        try {
            await fn();
            await load();
            setVersion((v) => v + 1);
            setResult({ kind, notice: done });
            onChanged?.();
        } catch (err) {
            // A refused step went through in part (one plan's renewal paused, another's not): show what's saved now
            if (err instanceof Refused) {
                await load().catch(() => {});
                setVersion((v) => v + 1);
                onChanged?.();
            }
            setResult({ kind, error: err instanceof Refused ? err.message : apiError(err) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <OrgModal isOpen wide onClose={onClose} title={org ? `Licenses: ${org.OrgName}` : "Licenses"}
                  footer={<button type="button" className={s.btnSecondary} onClick={onClose}>Close</button>}>
            <Alert onClose={() => setError("")}>{error}</Alert>
            {!org || !data ? (
                !error && (
                    <div className="space-y-3" aria-busy="true">
                        {[0, 1].map((i) => <Skeleton key={i} height={150} borderRadius={16}
                                                     baseColor={s.dark ? "#334155" : "#e2e8f0"} highlightColor={s.dark ? "#475569" : "#f1f5f9"} />)}
                    </div>
                )
            ) : (
                <div className="space-y-6">
                    <div className="flex flex-wrap items-center gap-2">
                        <Pill tone="primary">{ORG_TYPE_LABEL[org.OrgType] || org.OrgType}</Pill>
                        {org.Status !== "active" && <Pill tone={org.Status === "pending" ? "warning" : "danger"}>{org.Status}</Pill>}
                    </div>
                    {KINDS.map((kind) => (
                        <KindSection key={`${kind.key}-${version}`} kind={kind} org={org} pricing={data.pricing[kind.key] || null}
                                     requests={data.requests.filter((r) => r.LicenseType === kind.key)} busy={busy}
                                     run={(fn, done) => run(kind.key, fn, done)}
                                     result={result?.kind === kind.key ? result : null} onResultClose={() => setResult(null)} />
                    ))}
                </div>
            )}
        </OrgModal>
    );
}

/** One license type: now, what's waiting, the number it should have, and what that does. */
function KindSection({ kind, org, pricing, requests, busy, run, result, onResultClose }) {
    const s = useOrgStyles();
    // a change's result shows at the end of this section: bring it into view (the window scrolls)
    const resultRef = useRef(null);
    useEffect(() => {
        if (result) resultRef.current?.scrollIntoView({ block: "nearest" });
    }, [result]);
    const partner = org.OrgType === "nonprofit";
    const word = partner ? "free" : "included";
    const base = Number(org[kind.base] || 0);
    const purchased = Number(org[kind.purchased] || 0);
    const used = Number(org[kind.used] || 0);
    const plan = requests.find((r) => r.Status === "active" && r.AutoRenews);
    const open = requests.find((r) => r.Status === "pending" || r.Status === "approved");
    // Licenses added outside the plan that carry on (given with no price, or bought without renewal); each can end here
    const grants = requests.filter((r) => r.Status === "active" && !r.AutoRenews);
    const added = grants.reduce((n, r) => n + Number(r.Quantity || 0), 0);
    const planNow = plan ? Number(plan.Quantity) : 0;
    // A plan or grant that ended still counts until the month is over: this month only
    const ending = Math.max(0, purchased - planNow - added);
    const periodEnd = requests.find((r) => r.PeriodEnd)?.PeriodEnd;
    const endingOn = periodEnd ? formatDate(periodEnd) : "the end of this month";
    const nextMonth = periodEnd ? formatDate(dayAfter(periodEnd)) : "next month";
    const scheduled = plan && plan.ScheduledQuantity != null && !plan.CancelAtPeriodEnd;
    const planNext = !plan || plan.CancelAtPeriodEnd ? 0 : Number(plan.ScheduledQuantity ?? plan.Quantity);
    const nowTotal = base + purchased;
    const nextTotal = base + added + planNext;
    const floor = base + added; // not paid through the plan: the number can't go below it here
    const renewsOn = plan?.CurrentPeriodEnd ? formatDate(plan.CurrentPeriodEnd) : null;
    const endsOn = plan?.CurrentPeriodEnd ? formatDate(monthEnd(plan.CurrentPeriodEnd)) : null;
    // `pricing` is the plan's own tier when there's a plan. A plan keeps the price it was bought at: when that
    // tier's price, discount or billing changed, or it isn't sold anymore, its number can't change (the backend's
    // PlanChangeBlocked), only its renewal; the free/included number still can.
    const blocked = plan ? (plan.PlanChangeBlocked || (pricing ? null : "NO_GROUP_PRICE")) : null;
    const sellable = Boolean(pricing) && !blocked;
    const period = sellable ? pricing.BillingMode : plan?.BillingMode;
    //old // what the plan bills this period (with fewer seats scheduled, its saved price is the next renewal's)
    //old const planNowTotal = !plan ? null : !scheduled ? Number(plan.TotalAmount || 0) : sellable ? quote(pricing, planNow).total : null;
    // what the plan bills this period (with fewer seats scheduled, its saved price is the next renewal's, also once
    // its renewal is off or paused)
    const planNowTotal = !plan ? null : plan.ScheduledQuantity == null ? Number(plan.TotalAmount || 0) : sellable ? quote(pricing, planNow).total : null;
    // What the plan still gives next month, by the backend's rule (_next_month_seats_sql): with its renewal off it
    // lasts until its paid period ends, and fewer seats scheduled count only from the renewal
    const nextStart = periodEnd ? dayAfter(periodEnd) : null;
    const planEndDay = plan?.CurrentPeriodEnd ? String(plan.CurrentPeriodEnd).slice(0, 10) : null;
    const planLastsPastMonth = Boolean(planEndDay && nextStart && planEndDay >= nextStart);
    const planNextMonth = !plan ? 0 : !planLastsPastMonth && plan.CancelAtPeriodEnd ? 0
        : !planLastsPastMonth && plan.ScheduledQuantity != null ? Number(plan.ScheduledQuantity) : planNow;
    const active = org.Status === "active";
    // More paid licenses need an active org; fewer need only a plan that renews with nothing waiting
    const canAdd = active && !open && !plan?.CancelAtPeriodEnd;
    const canLower = Boolean(plan) && !open && !plan.CancelAtPeriodEnd;
    // a suspended org's plan that still renews (Stripe didn't pause it when it was suspended)
    const pauseNeeded = Boolean(plan) && org.Status === "suspended" && !plan.CancelAtPeriodEnd;

    // The number box keeps what's typed: it's checked while typing and put in range only on leaving the box
    const stepper = sellable || !plan; // a plan whose number can't change has only its renewal (and the free number)
    const min = sellable ? floor : added + planNext;
    const max = !sellable ? MAX_LICENSES : canAdd ? floor + MAX_PLAN_SEATS : canLower ? floor + planNow : floor;
    const [text, setText] = useState(String(nextTotal));
    const typed = /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;
    const target = typed != null && typed >= min && typed <= max ? typed : null;
    const clamp = (n) => Math.max(min, Math.min(max, n));
    const setNumber = (n) => setText(String(clamp(n)));
    const settle = () => setText(String(typed == null ? nextTotal : clamp(typed)));
    const [baseEdit, setBaseEdit] = useState(null); // text while the included/free number is being changed
    const [note, setNote] = useState(""); // kept with licenses set up here, for Pono Pilot's records
    //old const parts = (total) => {
    //old     const paid = total - base - added;
    //old     return [`${base} ${word}`, added > 0 ? `${added} added` : null, paid > 0 ? `${paid} paid` : null].filter(Boolean).join(" + ");
    //old };
    //old const nowParts = [`${base} ${word}`, added > 0 ? `${added} added` : null, planNow > 0 ? `${planNow} paid` : null,
    //old     ending > 0 ? `${ending} ending ${endingOn}` : null].filter(Boolean).join(" + ");
    // (the split the org's Licenses page says too: licenseParts)
    const parts = (total) => licenseParts(org.OrgType, base, added, total - base - added);
    const nowParts = licenseParts(org.OrgType, base, added, planNow) + (ending > 0 ? ` + ${ending} ending ${endingOn}` : "");
    const [confirm, setConfirm] = useState(null); // "stop" or an added row being ended: asked here first
    const planSeats = plan ? Number(plan.ScheduledQuantity ?? plan.Quantity) : 0;
    const blockedText = !blocked ? null : `${!pricing || blocked === "NO_GROUP_PRICE"
        ? `${plan.TierName ? `The ${plan.TierName} plan` : "Its plan"} isn't sold to organizations anymore`
        : blocked === "BILLING_CHANGED"
            ? `It's billed ${plan.BillingMode} and the ${pricing.TierName} plan is now billed ${pricing.BillingMode}`
            : Math.abs(Number(plan.UnitPrice) - Number(pricing.UnitPrice)) >= 0.005
                ? `It was bought at ${money(plan.UnitPrice)} a license and the ${pricing.TierName} price is now ${money(pricing.UnitPrice)}`
                : `It was bought with ${pctText(plan.PercentOff)} off and the ${pricing.TierName} discount for ${planSeats} licenses is now ${pctText(quote(pricing, planSeats).pct)}`
    }, so its number of licenses can't change. To change it, stop renewing; once it ends, set up new licenses here.`;
    const stopRenewal = () => run(() => api.post(`/org/${org.OrgID}/license-requests/${plan.RequestID}/auto-renew`, { renew: false }),
        //old `Renewal is off. ${org.OrgName}'s paid ${kind.key} licenses last until ${endsOn}.`);
        `Renewal is off. ${org.OrgName}'s paid ${LICENSE_TYPE_LABEL[kind.key]} licenses last until ${endsOn}.`);
    // A lower number never takes a license away (Evan, Sep 28): it only stops new ones until fewer are in use
    const keepsText = (limit) => (limit < used
        //old ? `${used} ${kind.key} ${used === 1 ? "license is" : "licenses are"} in use. Nobody loses one, but no new ones can be given out`
        ? `${used} ${LICENSE_TYPE_LABEL[kind.key]} ${used === 1 ? "license is" : "licenses are"} in use. Nobody loses one, but no new ones can be given out`
            + (limit === 0 ? "." : limit === 1 ? " until none are in use." : ` until fewer than ${limit} are in use.`) : null);

    // What the chosen number does, and the button that does it
    let change = null;
    if (stepper && target != null && target !== nextTotal) {
        const seats = target - floor; // paid seats on the plan from then on
        if (!sellable) {
            change = {
                big: `${lic(target)}, no charge`,
                //old sub: `There's no price for ${kind.key} licenses, so the number is simply set`
                //old     + (ending > 0 ? ` from ${nextMonth} (the ${ending} ending ${endingOn} still count this month).` : "."),
                //old sub: `There's no price for ${kind.key} licenses, so the number is simply set`
                sub: `There's no price for ${LICENSE_TYPE_LABEL[kind.key]} licenses, so the number is simply set`
                    + (ending > 0 ? ` from ${nextMonth} (the ${ending} ending ${endingOn} still count this month).` : ".")
                    + (keepsText(target - added - planNext + purchased) ? ` ${keepsText(target - added - planNext + purchased)}` : ""),
                label: `Change to ${lic(target)}`,
                // the included number: the total less what else carries on into next month
                go: () => api.put(`/org/${org.OrgID}`, { [kind.field]: target - added - planNext }),
                done: `${org.OrgName} has ${lic(target)} for ${kind.plural}${ending > 0 ? ` from ${nextMonth}` : ""}.`,
            };
        } else if (!plan || seats > planNow) {
            const more = seats - planNow;
            const q = quote(pricing, seats);
            const cheaper = cheaperBigger(pricing, seats);
            if (plan && scheduled) {
                change = { blocked: `Fewer licenses are already set from the renewal on ${renewsOn}. Keep ${lic(floor + planNow)} first, then add.` };
            } else if (cheaper) {
                change = {
                    blocked: `${lic(target)} would cost ${money(q.total)} ${per(period)}, more than ${floor + cheaper.seats} (${money(cheaper.total)}), `
                        + `because the ${cheaper.seats}+ discount covers every paid license.`,
                    pick: floor + cheaper.seats,
                };
            } else if (plan) {
                const diff = Math.round((q.total - planNowTotal) * 100) / 100;
                change = {
                    big: `${lic(target)} (${parts(target)}): ${money(q.total)} ${per(period)} instead of ${money(planNowTotal)}`
                        + (diff > 0 ? ` (+${money(diff)})` : " (no extra cost)"),
                    sub: `${org.OrgName} sees “Add to my plan” on its Licenses page and pays for the rest of this billing period`
                        + `${renewsOn ? ` (until ${renewsOn})` : ""}; then it renews at ${money(q.total)} ${per(period)}.`,
                    label: `Change to ${lic(target)}`,
                    setup: true,
                    go: () => api.post(`/org/admin/orgs/${org.OrgID}/license-requests`, { licenseType: kind.key, quantity: more, note: note.trim() || undefined }),
                    //old done: `${more} more ${kind.key} ${more === 1 ? "license is" : "licenses are"} set up. ${org.OrgName} sees “Add to my plan” on its Licenses page.`,
                    done: `${more} more ${LICENSE_TYPE_LABEL[kind.key]} ${more === 1 ? "license is" : "licenses are"} set up. ${org.OrgName} sees “Add to my plan” on its Licenses page.`,
                };
            } else {
                change = {
                    big: `${lic(target)} (${parts(target)}): ${money(q.total)} ${per(period)}`,
                    sub: `${org.OrgName} sees Pay now on its Licenses page. Once it pays, the licenses are added and renew automatically every ${period === "yearly" ? "year" : "month"}.`,
                    label: `Change to ${lic(target)}`,
                    setup: true,
                    go: () => api.post(`/org/admin/orgs/${org.OrgID}/license-requests`, { licenseType: kind.key, quantity: more, note: note.trim() || undefined }),
                    //old done: `${more} ${kind.key} ${more === 1 ? "license is" : "licenses are"} set up. ${org.OrgName} sees Pay now on its Licenses page.`,
                    done: `${more} ${LICENSE_TYPE_LABEL[kind.key]} ${more === 1 ? "license is" : "licenses are"} set up. ${org.OrgName} sees Pay now on its Licenses page.`,
                };
            }
        } else if (seats === planNow) {
            change = {
                big: `${lic(target)} from the renewal on ${renewsOn}: ${money(quote(pricing, planNow).total)} ${per(period)}`,
                sub: `This cancels the change to ${lic(nextTotal)} set for the renewal.`,
                label: `Keep ${lic(target)}`,
                go: () => api.post(`/org/${org.OrgID}/license-requests/${plan.RequestID}/seats`, { seats: planNow }),
                done: `${org.OrgName} keeps ${lic(target)} for ${kind.plural}.`,
            };
        } else if (seats >= 1) {
            const q = quote(pricing, seats);
            const nowFull = quote(pricing, planNow).total;
            const keeps = floor + planNow; // until the renewal (licenses ending this month count only until then)
            if (q.total > nowFull) {
                let fits = seats - 1;
                while (fits >= 1 && quote(pricing, fits).total > nowFull) fits -= 1;
                change = {
                    //old blocked: `${lic(target)} would cost ${money(q.total)} ${per(period)} from the renewal, more than the ${nowTotal} it has now `
                    //old     + `(${money(nowFull)}), because the volume discount covers every paid license. Keep ${floor + planNow}`
                    blocked: `${lic(target)} would cost ${money(q.total)} ${per(period)} from the renewal, more than the ${keeps} it has now `
                        + `(${money(nowFull)}), because the volume discount covers every paid license. Keep ${keeps}`
                        + (fits >= 1 ? `, or pick ${floor + fits} or fewer.` : "."),
                    pick: fits >= 1 ? floor + fits : null,
                };
            } else {
                change = {
                    big: `From the renewal on ${renewsOn}: ${lic(target)} (${parts(target)}), ${money(q.total)} ${per(period)}`,
                    //old sub: `${org.OrgName} keeps ${nowTotal} until then; nothing is refunded.`,
                    sub: `${org.OrgName} keeps ${keeps} until then${ending > 0 ? ` (and the ${ending} ending ${endingOn} until that day)` : ""}; nothing is refunded.`,
                    label: `Change to ${lic(target)}`,
                    go: () => api.post(`/org/${org.OrgID}/license-requests/${plan.RequestID}/seats`, { seats }),
                    done: `From ${renewsOn}, ${org.OrgName} has ${lic(target)} for ${kind.plural}.`,
                };
            }
        } else {
            change = {
                big: `The ${planNow} paid ${planNow === 1 ? "license stops" : "licenses stop"} at the renewal on ${renewsOn}`,
                sub: `${org.OrgName} keeps them until ${endsOn}, then has ${floor} ${word} ${floor === 1 ? "license" : "licenses"}. Nothing is refunded.`,
                label: "Stop the paid licenses at renewal",
                stop: true,
            };
        }
    }
    const numberLocked = busy || min >= max;
    // Community Partner packages: 25 free, 50, 100 in all (with the Oct 16 setup: 25 free, nothing added)
    const packages = partner && kind.key === "student" && sellable && base === PARTNER_FREE && added === 0 ? PARTNER_PACKAGES : null;

    // a whole number from 0 (a blank box isn't 0: Save waits for a number)
    const baseOk = /^\d+$/.test(String(baseEdit ?? "").trim()) && Number(String(baseEdit).trim()) <= MAX_LICENSES;
    const saveBase = () => {
        if (!baseOk) return;
        const n = Number(String(baseEdit).trim());
        //old run(() => api.put(`/org/${org.OrgID}`, { [kind.field]: n }), `${org.OrgName} has ${n} ${word} ${kind.key} ${n === 1 ? "license" : "licenses"}.`);
        run(() => api.put(`/org/${org.OrgID}`, { [kind.field]: n }), `${org.OrgName} has ${n} ${word} ${LICENSE_TYPE_LABEL[kind.key]} ${n === 1 ? "license" : "licenses"}.`);
    };

    return (
        <section aria-labelledby={`lic-${kind.key}`} className={`rounded-2xl border p-4 ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 id={`lic-${kind.key}`} className={`text-base font-bold ${s.text}`}>{kind.title}</h4>
                <span className={`text-xs ${s.muted}`}>{used} assigned this month</span>
            </div>

            {/* Now */}
            <div className={`mt-3 flex flex-wrap items-baseline justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${s.subPanel}`}>
                <span className={s.body}>
                    Now: <strong className={s.text}>{lic(nowTotal)}</strong> ({nowParts})
                </span>
            </div>
            {(sellable || blocked) && (
                <div className="mt-1.5 text-xs">
                    {baseEdit === null ? (
                        <button type="button" className={`${s.btnLink} !text-xs`} disabled={busy} onClick={() => setBaseEdit(String(base))}>
                            Change the {base} {word} {base === 1 ? "license" : "licenses"}
                        </button>
                    ) : (
                        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); saveBase(); }}>
                            {/* old <label htmlFor={`lic-base-${kind.key}`} className={s.muted}>{word[0].toUpperCase() + word.slice(1)} {kind.key} licenses</label> */}
                            <label htmlFor={`lic-base-${kind.key}`} className={s.muted}>{word[0].toUpperCase() + word.slice(1)} {LICENSE_TYPE_LABEL[kind.key]} licenses</label>
                            <input id={`lic-base-${kind.key}`} type="number" min="0" max={MAX_LICENSES} className={`${s.input} !w-24`}
                                   value={baseEdit} onChange={(e) => setBaseEdit(e.target.value)} />
                            <button type="submit" className={s.btnSm} disabled={busy || !baseOk}>Save</button>
                            <button type="button" className={s.btnSm} onClick={() => setBaseEdit(null)}>Cancel</button>
                            {!baseOk && <span className={s.dark ? "text-amber-300" : "text-amber-700"}>Enter a number from 0 to {MAX_LICENSES}.</span>}
                            {baseOk && keepsText(Number(String(baseEdit).trim()) + purchased) && (
                                <span role="status" className={`basis-full ${s.dark ? "text-amber-300" : "text-amber-700"}`}>
                                    {keepsText(Number(String(baseEdit).trim()) + purchased)}
                                </span>
                            )}
                        </form>
                    )}
                </div>
            )}

            {/* What's already set and waiting */}
            {!active && sellable && (
                <p className={`mt-3 text-sm ${s.dark ? "text-amber-300" : "text-amber-700"}`}>
                    {org.Status === "pending" ? `Approve ${org.OrgName} first to add paid licenses.` : `${org.OrgName} is suspended: reactivate it first to add licenses.`}
                </p>
            )}
            {plan && (
                <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${plan.CancelAtPeriodEnd || pauseNeeded || blocked
                    ? (s.dark ? "border-amber-800 bg-amber-900/20" : "border-amber-200 bg-amber-50") : s.subPanel}`}>
                    {confirm === "stop" ? (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className={s.text}>
                                Stop renewing? The {planNow} paid {planNow === 1 ? "license stops" : "licenses stop"} at the renewal on {renewsOn};
                                {" "}{org.OrgName} keeps them until {endsOn}, and nothing is refunded.
                            </span>
                            <span className="flex gap-2">
                                <button type="button" className={s.btnSm} disabled={busy} onClick={stopRenewal}>Stop renewing</button>
                                <button type="button" className={s.btnSm} onClick={() => setConfirm(null)}>Keep renewing</button>
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className={s.text}>
                                Paid plan: <strong>{lic(planNow)}</strong>{planNowTotal != null && <>, {money(planNowTotal)} {per(plan.BillingMode)}</>}
                                <span className={`block text-xs ${s.muted}`}>
                                    {plan.CancelAtPeriodEnd ? `Renewal is off: the ${planNow} paid licenses end on ${endsOn}.`
                                        + (plan.ScheduledQuantity != null ? ` Turning it back on renews it with ${plan.ScheduledQuantity} paid.` : "")
                                        : pauseNeeded ? `${org.OrgName} is suspended, but they still renew on ${renewsOn}: Stripe didn't pause them.`
                                            : scheduled ? `Renews ${renewsOn} with ${planNext} paid (${lic(nextTotal)} in all), ${money(plan.TotalAmount)} ${per(plan.BillingMode)}.`
                                                : renewsOn ? `Renews ${renewsOn}.` : "Renews automatically."}
                                    {plan.CancelAtPeriodEnd && !active ? ` Reactivate ${org.OrgName} first to turn it back on.` : ""}
                                </span>
                            </span>
                            {plan.CancelAtPeriodEnd ? (
                                <button type="button" className={s.btnSm} disabled={busy || !active}
                                        onClick={() => run(() => api.post(`/org/${org.OrgID}/license-requests/${plan.RequestID}/auto-renew`, { renew: true }), "Renewal is back on.")}>
                                    Turn renewal back on
                                </button>
                            ) : pauseNeeded ? (
                                // what suspending does: renewal stops at the end of the paid period, and reactivating turns it back on
                                <button type="button" className={s.btnSm} disabled={busy}
                                        onClick={() => run(async () => {
                                            const res = await api.put(`/org/${org.OrgID}`, { status: "suspended" });
                                            //old if (res.data.billingNotStopped?.length) throw new Refused("Stripe didn't pause it this time either. Try again in a few minutes.");
                                            // only this plan: suspending pauses every plan, and another one may still fail
                                            if (res.data.billingNotStopped?.includes(plan.RequestID)) throw new Refused("Stripe didn't pause it this time either. Try again in a few minutes.");
                                        }, `Renewal is paused while ${org.OrgName} is suspended: the paid licenses last until ${endsOn}, and reactivating it turns renewal back on.`)}>
                                    Pause renewal
                                </button>
                            ) : (
                                <button type="button" className={s.btnSm} disabled={busy} onClick={() => setConfirm("stop")}>Stop renewing</button>
                            )}
                        </div>
                    )}
                    {blocked && <p className={`mt-1 text-xs ${s.dark ? "text-amber-200" : "text-amber-800"}`}>{blockedText}</p>}
                </div>
            )}
            {open && (
                <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${s.dark ? "border-amber-800 bg-amber-900/20" : "border-amber-200 bg-amber-50"}`}>
                    <span className={s.text}>
                        {open.Status === "pending"
                            ? <>Waiting for approval: {open.Quantity} {open.AddsToPlan ? "more " : ""}licenses (License requests tab).</>
                            //old : <>Waiting for {org.OrgName} to pay: {open.Quantity} {open.AddsToPlan ? "more " : ""}licenses
                            //old     {open.TotalAmount != null && (Number(open.TotalAmount) > 0 ? <> (+{money(open.TotalAmount)} {per(open.BillingMode)})</> : " (no extra cost)")}.</>}
                            : open.QuoteChanged
                                // its price changed since it was set up (a discount step in Edit Tier): it can't be paid as set up
                                // (a first purchase too now, so 'more' only for seats added to the plan, as the lines below)
                                ? <>The {open.Quantity} {open.AddsToPlan ? "more " : ""}licenses waiting for {org.OrgName} to pay can't be paid anymore: their price changed since they were set up.</>
                                // its Pay by day is over: paying is refused (APPROVAL_EXPIRED), and it holds back a new set-up until it's cancelled
                                : open.PayByPassed
                                    ? <>The {open.Quantity} {open.AddsToPlan ? "more " : ""}licenses waiting for {org.OrgName} to pay can't be paid anymore: they had to be paid by {formatDate(open.PayBy)}.</>
                                    : <>Waiting for {org.OrgName} to pay: {open.Quantity} {open.AddsToPlan ? "more " : ""}licenses
                                        {open.TotalAmount != null && (Number(open.TotalAmount) > 0 ? <> (+{money(open.TotalAmount)} {per(open.BillingMode)})</> : " (no extra cost)")}.</>}
                        {/* old <span className={`block text-xs ${s.muted}`}>Change the number once it's paid, or cancel it.</span> */}
                        <span className={`block text-xs ${s.muted}`}>
                            {/* (for a plan whose own price changed since it was bought, nothing can be added to it before it ends,
                                so they aren't set up again: the plan's line above says how its number changes) */}
                            {open.QuoteChanged && open.AddsToPlan && blocked
                                ? "Cancel it: this plan can't get more licenses before it ends (stop renewing, then set up new ones once it ends)."
                                : open.QuoteChanged ? "Cancel it, then set them up again at today's price."
                                : open.PayByPassed ? "Cancel it, then set them up again." : "Change the number once it's paid, or cancel it."}
                        </span>
                    </span>
                    <button type="button" className={s.btnSm} disabled={busy}
                            onClick={() => run(() => api.delete(`/org/${org.OrgID}/license-requests/${open.RequestID}`), "The request was cancelled.")}>
                        Cancel it
                    </button>
                </div>
            )}
            {grants.map((g) => {
                const qty = Number(g.Quantity || 0);
                return (
                    <div key={g.RequestID} className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${s.subPanel}`}>
                        {confirm === g.RequestID ? (
                            <>
                                <span className={s.text}>
                                    {/* old End these {lic(qty)}? {org.OrgName} keeps them through {endingOn}, then has {lic(nextTotal - qty)}. */}
                                    {/* next month's number, as the backend counts it: a plan with its renewal off or fewer seats
                                        scheduled still gives its seats until its paid period ends or renews */}
                                    End these {lic(qty)}? {org.OrgName} keeps them through {endingOn}, then has {lic(base + added - qty + planNextMonth)}
                                    {planNextMonth !== planNext && <> until {plan.CancelAtPeriodEnd ? endsOn : renewsOn} ({lic(nextTotal - qty)} after)</>}.
                                </span>
                                <span className="flex gap-2">
                                    <button type="button" className={s.btnSm} disabled={busy}
                                            onClick={() => run(() => api.post(`/org/admin/license-requests/${g.RequestID}/end`),
                                                //old `The ${qty} added ${kind.key} ${qty === 1 ? "license ends" : "licenses end"} on ${endingOn}.`)}>
                                                `The ${qty} added ${LICENSE_TYPE_LABEL[kind.key]} ${qty === 1 ? "license ends" : "licenses end"} on ${endingOn}.`)}>
                                        End them
                                    </button>
                                    <button type="button" className={s.btnSm} onClick={() => setConfirm(null)}>Keep them</button>
                                </span>
                            </>
                        ) : (
                            <>
                                <span className={s.text}>
                                    {lic(qty)} added {Number(g.TotalAmount) > 0 ? `(paid ${money(g.TotalAmount)}, no renewal)` : "by Pono Pilot, no charge"}
                                </span>
                                <button type="button" className={s.btnSm} disabled={busy} onClick={() => setConfirm(g.RequestID)}>End</button>
                            </>
                        )}
                    </div>
                );
            })}
            {/* The number it should have */}
            <div className="mt-4 space-y-3">
                {stepper && (<>
                    {packages && (
                        <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label="Community Partner packages">
                            {packages.map((n) => {
                                const paid = n - PARTNER_FREE;
                                const on = typed === n;
                                return (
                                    <button key={n} type="button" aria-pressed={on} disabled={numberLocked || n < min || n > max} onClick={() => setText(String(n))}
                                            className={`rounded-xl border-2 p-3 text-left transition-colors disabled:opacity-50 ${on
                                                ? (s.dark ? "border-primary-400 bg-primary-900/30" : "border-primary-500 bg-primary-50")
                                                : (s.dark ? "border-slate-600 hover:border-slate-500" : "border-slate-200 hover:border-slate-300")}`}>
                                        <span className={`block text-[11px] font-semibold uppercase tracking-wide ${s.muted}`}>{n === nowTotal && ending === 0 ? "Now" : " "}</span>
                                        <span className={`block font-bold ${s.text}`}>{n} licenses</span>
                                        <span className={`block text-xs ${s.muted}`}>{paid === 0 ? "Free" : `${money(quote(pricing, paid).total)} ${per(period)}`}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <label htmlFor={`lic-total-${kind.key}`} className={s.body}>{packages ? "Or another number:" : "Licenses in all:"}</label>
                        <div className={`inline-flex items-center overflow-hidden rounded-lg border ${s.dark ? "border-slate-600" : "border-slate-300"}`}>
                            <button type="button" aria-label="One fewer" disabled={numberLocked || (typed ?? nextTotal) <= min}
                                    onClick={() => setNumber((typed ?? nextTotal) - 1)} className={`px-2.5 py-2 disabled:opacity-40 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-50"}`}>
                                <Minus size={14} />
                            </button>
                            <input id={`lic-total-${kind.key}`} type="number" min={min} max={max} disabled={numberLocked}
                                   value={text} onChange={(e) => setText(e.target.value)} onBlur={settle}
                                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); settle(); } }}
                                   className={`w-20 border-x px-2 py-1.5 text-center text-sm tabular-nums ${s.dark ? "border-slate-600 bg-slate-700 text-slate-100" : "border-slate-300 bg-white text-slate-800"}`} />
                            <button type="button" aria-label="One more" disabled={numberLocked || (typed ?? nextTotal) >= max}
                                    onClick={() => setNumber((typed ?? nextTotal) + 1)} className={`px-2.5 py-2 disabled:opacity-40 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-50"}`}>
                                <Plus size={14} />
                            </button>
                        </div>
                        {text !== String(nextTotal) && !numberLocked && (
                            <button type="button" className={`${s.btnLink} !text-xs`} onClick={() => setText(String(nextTotal))}>Reset</button>
                        )}
                    </div>
                    {added > 0 && !numberLocked && (typed ?? nextTotal) <= min && (
                        <p className={`text-xs ${s.muted}`}>To go below {min}, end the {added} added licenses above.</p>
                    )}
                    {target == null && !numberLocked && (
                        <p role="status" className={`text-xs ${s.dark ? "text-amber-300" : "text-amber-700"}`}>Enter a number from {min} to {max}.</p>
                    )}

                    {change?.blocked ? (
                        <div role="status" className={`rounded-xl border px-3 py-2 text-sm ${s.dark ? "border-amber-800 bg-amber-900/20 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                            {change.blocked}
                            {change.pick != null && (
                                <button type="button" className={`${s.btnLink} ml-1 !text-sm`} onClick={() => setText(String(change.pick))}>Pick {change.pick}</button>
                            )}
                        </div>
                    ) : change && (
                        <div className={`rounded-xl border px-3 py-2 text-sm ${s.dark ? "border-primary-800 bg-primary-900/20" : "border-primary-200 bg-primary-50"}`}>
                            <span className={`block font-semibold ${s.text}`}>{change.big}.</span>
                            <span className={`block text-xs ${s.body}`}>{change.sub}</span>
                        </div>
                    )}
                    {change?.setup && (
                        <Field label="Note (optional)" htmlFor={`lic-note-${kind.key}`} hint="For Pono Pilot's records, e.g. what was agreed with the organization.">
                            <textarea id={`lic-note-${kind.key}`} rows={2} maxLength={500} className={s.input} value={note} onChange={(e) => setNote(e.target.value)} />
                        </Field>
                    )}
                    {change && !change.blocked && (
                        <div className="flex justify-end">
                            <button type="button" className={s.btnPrimary} disabled={busy} onClick={change.stop ? stopRenewal : () => run(change.go, change.done)}>
                                {busy && <Loader2 size={14} className="animate-spin" />} {change.label}
                            </button>
                        </div>
                    )}
                </>)}
                {result && (
                    <div ref={resultRef}>
                        <Alert kind={result.error ? "error" : "success"} onClose={onResultClose}>{result.error || result.notice}</Alert>
                    </div>
                )}

                <p className={`text-xs ${s.muted}`}>
                    {pricing ? <>Prices come from the {pricing.TierName} plan. </> : <>{kind.title} have no price: the plan for licensed {kind.plural} isn't sold to organizations. </>}
                    <Link to="/admin/memberships" className={`${s.btnLink} !text-xs`}>Edit prices in Membership Management</Link>
                </p>
            </div>
        </section>
    );
}
